export interface StoryGalaxyNode {
  id: string
  name: string
  slug: string
  type: 'genre' | 'subgenre' | 'romance_subgenre' | 'story_engine' | 'tone'
  glossaryDefinition: string
  shortDefinition: string
  primaryClusterId: string
  secondaryClusterIds: string[]
  nearbyNodeIds: string[]
  parentId?: string
  popularStoryEngineIds?: string[]
  popularToneIds?: string[]
}

export interface MoodCluster {
  id: string
  name: string
  slug: string
  shortDescription: string
  visualTheme:
    | 'violet-cosmic'
    | 'crimson-shadow'
    | 'peach-gold'
    | 'electric-blue'
    | 'emerald-moon'
    | 'indigo-neon'
    | 'sepia-gold'
  featuredNodeIds: string[]
}

export interface GravityEdge {
  id: string
  sourceNodeId: string
  targetNodeId: string
  relationshipType: string
  weight: number
  reason: string
}

export interface ConstellationView {
  clusterId: string
  centerNodeId: string
  visibleNodeIds: string[]
}

export interface SuggestedStoryStack {
  id: string
  label: string
  nodeIds: string[]
}

export interface StoryGalaxyData {
  moodClusters: MoodCluster[]
  nodes: StoryGalaxyNode[]
  gravityEdges: GravityEdge[]
  constellationViews: ConstellationView[]
  suggestedStoryStacks: SuggestedStoryStack[]
}

export const storyGalaxyData: StoryGalaxyData = {
  moodClusters: [
    {
      id: 'cluster-magical-worlds',
      name: 'Magical Worlds',
      slug: 'magical-worlds',
      shortDescription:
        'Stories centered on magic, supernatural systems, fantasy realms, and imagined worlds.',
      visualTheme: 'violet-cosmic',
      featuredNodeIds: [
        'genre-fantasy',
        'subgenre-urban-fantasy',
        'subgenre-epic-fantasy',
        'romance-fantasy-romance',
        'romance-paranormal-romance',
      ],
    },
    {
      id: 'cluster-dark-dangerous',
      name: 'Dark & Dangerous',
      slug: 'dark-dangerous',
      shortDescription:
        'Stories driven by danger, obsession, moral ambiguity, high tension, and darker emotional intensity.',
      visualTheme: 'crimson-shadow',
      featuredNodeIds: [
        'romance-dark-romance',
        'romance-mafia-romance',
        'subgenre-dark-fantasy',
        'subgenre-psychological-thriller',
        'romance-romantic-suspense',
      ],
    },
    {
      id: 'cluster-cozy-comfort',
      name: 'Cozy & Comfort',
      slug: 'cozy-comfort',
      shortDescription:
        'Warm, community-focused, emotionally comforting stories with softer tension and strong connection.',
      visualTheme: 'peach-gold',
      featuredNodeIds: [
        'romance-small-town-romance',
        'subgenre-cozy-mystery',
        'romance-romantic-comedy',
        'genre-contemporary-fiction',
      ],
    },
    {
      id: 'cluster-high-stakes-suspense',
      name: 'High Stakes & Suspense',
      slug: 'high-stakes-suspense',
      shortDescription:
        'Fast-moving stories built around danger, investigation, urgency, and survival.',
      visualTheme: 'electric-blue',
      featuredNodeIds: [
        'genre-thriller',
        'genre-mystery',
        'subgenre-crime-thriller',
        'subgenre-detective-fiction',
        'romance-romantic-suspense',
      ],
    },
    {
      id: 'cluster-monsters-myth',
      name: 'Monsters & Myth',
      slug: 'monsters-myth',
      shortDescription:
        'Stories involving mythological beings, non-human characters, supernatural creatures, and primal bonds.',
      visualTheme: 'emerald-moon',
      featuredNodeIds: [
        'romance-monster-romance',
        'romance-paranormal-romance',
        'subgenre-mythic-fantasy',
        'subgenre-supernatural-horror',
      ],
    },
    {
      id: 'cluster-cosmic-futuristic',
      name: 'Cosmic & Futuristic',
      slug: 'cosmic-futuristic',
      shortDescription:
        'Stories set in futuristic, technological, spacefaring, or alien environments.',
      visualTheme: 'indigo-neon',
      featuredNodeIds: [
        'genre-science-fiction',
        'subgenre-space-opera',
        'subgenre-cyberpunk',
        'romance-science-fiction-romance',
        'romance-alien-romance',
      ],
    },
    {
      id: 'cluster-historical-worlds',
      name: 'Historical Worlds',
      slug: 'historical-worlds',
      shortDescription:
        'Stories set in past eras, shaped by the customs, politics, and social rules of their time.',
      visualTheme: 'sepia-gold',
      featuredNodeIds: [
        'genre-historical-fiction',
        'romance-historical-romance',
      ],
    },
  ],

  nodes: [
    // ── Genres ──────────────────────────────────────────────
    {
      id: 'genre-fantasy',
      name: 'Fantasy',
      slug: 'fantasy',
      type: 'genre',
      glossaryDefinition:
        'Stories set in imagined worlds where magic, supernatural forces, or mythical creatures exist.',
      shortDefinition:
        'Imagined worlds shaped by magic or supernatural forces.',
      primaryClusterId: 'cluster-magical-worlds',
      secondaryClusterIds: ['cluster-monsters-myth'],
      nearbyNodeIds: [
        'subgenre-epic-fantasy',
        'subgenre-urban-fantasy',
        'subgenre-dark-fantasy',
        'romance-fantasy-romance',
        'romance-paranormal-romance',
      ],
      popularStoryEngineIds: ['engine-enemies-to-lovers', 'engine-fated-mates'],
      popularToneIds: ['tone-dark', 'tone-slow-burn'],
    },
    {
      id: 'genre-science-fiction',
      name: 'Science Fiction',
      slug: 'science-fiction',
      type: 'genre',
      glossaryDefinition:
        'Stories exploring speculative technology, futuristic societies, space travel, or scientific possibilities.',
      shortDefinition: 'Stories built around future tech, science, or space.',
      primaryClusterId: 'cluster-cosmic-futuristic',
      secondaryClusterIds: ['cluster-high-stakes-suspense'],
      nearbyNodeIds: [
        'subgenre-space-opera',
        'subgenre-cyberpunk',
        'romance-science-fiction-romance',
        'romance-alien-romance',
      ],
      popularStoryEngineIds: ['engine-forced-proximity'],
      popularToneIds: ['tone-slow-burn'],
    },
    {
      id: 'genre-mystery',
      name: 'Mystery',
      slug: 'mystery',
      type: 'genre',
      glossaryDefinition:
        'Stories centered around solving crimes, puzzles, or unexplained events.',
      shortDefinition: 'Stories built around solving a puzzle or crime.',
      primaryClusterId: 'cluster-high-stakes-suspense',
      secondaryClusterIds: ['cluster-cozy-comfort'],
      nearbyNodeIds: [
        'subgenre-detective-fiction',
        'subgenre-cozy-mystery',
        'genre-thriller',
        'romance-romantic-suspense',
      ],
      popularStoryEngineIds: ['engine-forced-proximity'],
      popularToneIds: ['tone-slow-burn'],
    },
    {
      id: 'genre-thriller',
      name: 'Thriller',
      slug: 'thriller',
      type: 'genre',
      glossaryDefinition:
        'Fast-paced stories driven by danger, suspense, and high-stakes conflict.',
      shortDefinition: 'Fast-paced stories fueled by danger and suspense.',
      primaryClusterId: 'cluster-high-stakes-suspense',
      secondaryClusterIds: ['cluster-dark-dangerous'],
      nearbyNodeIds: [
        'genre-mystery',
        'subgenre-crime-thriller',
        'subgenre-psychological-thriller',
        'romance-romantic-suspense',
      ],
      popularStoryEngineIds: [
        'engine-protector-romance',
        'engine-forced-proximity',
      ],
      popularToneIds: ['tone-dark'],
    },
    {
      id: 'genre-historical-fiction',
      name: 'Historical Fiction',
      slug: 'historical-fiction',
      type: 'genre',
      glossaryDefinition:
        'Stories set in real historical periods reflecting the culture, social rules, and events of the past.',
      shortDefinition: 'Stories set in past eras shaped by historical context.',
      primaryClusterId: 'cluster-historical-worlds',
      secondaryClusterIds: [],
      nearbyNodeIds: ['romance-historical-romance'],
      popularStoryEngineIds: [
        'engine-marriage-of-convenience',
        'engine-forbidden-love',
      ],
      popularToneIds: ['tone-slow-burn'],
    },
    {
      id: 'genre-contemporary-fiction',
      name: 'Contemporary Fiction',
      slug: 'contemporary-fiction',
      type: 'genre',
      glossaryDefinition:
        'Stories set in modern-day environments reflecting current life, society, and relationships.',
      shortDefinition: 'Stories set in present-day life and society.',
      primaryClusterId: 'cluster-cozy-comfort',
      secondaryClusterIds: [],
      nearbyNodeIds: [
        'romance-contemporary-romance',
        'romance-romantic-comedy',
        'romance-small-town-romance',
      ],
      popularStoryEngineIds: [
        'engine-friends-to-lovers',
        'engine-second-chance',
      ],
      popularToneIds: ['tone-sweet', 'tone-slow-burn'],
    },
    {
      id: 'genre-romance',
      name: 'Romance',
      slug: 'romance',
      type: 'genre',
      glossaryDefinition:
        'Stories where the romantic relationship is the central emotional focus of the narrative.',
      shortDefinition: 'Stories centered on the romantic relationship.',
      primaryClusterId: 'cluster-cozy-comfort',
      secondaryClusterIds: ['cluster-dark-dangerous', 'cluster-magical-worlds'],
      nearbyNodeIds: [
        'romance-contemporary-romance',
        'romance-fantasy-romance',
        'romance-romantic-suspense',
        'romance-dark-romance',
      ],
      popularStoryEngineIds: [
        'engine-enemies-to-lovers',
        'engine-friends-to-lovers',
        'engine-fake-dating',
      ],
      popularToneIds: ['tone-sweet', 'tone-slow-burn', 'tone-dark'],
    },

    // ── Subgenres ──────────────────────────────────────────
    {
      id: 'subgenre-epic-fantasy',
      name: 'Epic Fantasy',
      slug: 'epic-fantasy',
      type: 'subgenre',
      parentId: 'genre-fantasy',
      glossaryDefinition:
        'Large-scale fantasy stories involving expansive worlds, major conflicts, and broad casts of characters.',
      shortDefinition:
        'Large-scale fantasy with expansive worlds and major conflicts.',
      primaryClusterId: 'cluster-magical-worlds',
      secondaryClusterIds: ['cluster-historical-worlds'],
      nearbyNodeIds: [
        'genre-fantasy',
        'subgenre-mythic-fantasy',
        'romance-fantasy-romance',
      ],
      popularStoryEngineIds: [
        'engine-forbidden-love',
        'engine-marriage-of-convenience',
      ],
      popularToneIds: ['tone-slow-burn'],
    },
    {
      id: 'subgenre-urban-fantasy',
      name: 'Urban Fantasy',
      slug: 'urban-fantasy',
      type: 'subgenre',
      parentId: 'genre-fantasy',
      glossaryDefinition:
        'Fantasy stories set in modern cities or real-world environments where supernatural elements exist alongside everyday life.',
      shortDefinition:
        'Fantasy set in modern settings with supernatural elements.',
      primaryClusterId: 'cluster-magical-worlds',
      secondaryClusterIds: ['cluster-monsters-myth'],
      nearbyNodeIds: [
        'genre-fantasy',
        'romance-paranormal-romance',
        'romance-fantasy-romance',
      ],
      popularStoryEngineIds: [
        'engine-forced-proximity',
        'engine-enemies-to-lovers',
      ],
      popularToneIds: ['tone-dark'],
    },
    {
      id: 'subgenre-dark-fantasy',
      name: 'Dark Fantasy',
      slug: 'dark-fantasy',
      type: 'subgenre',
      parentId: 'genre-fantasy',
      glossaryDefinition:
        'Fantasy stories that explore darker themes, moral ambiguity, horror-like elements, or oppressive settings.',
      shortDefinition: 'Fantasy shaped by darker themes and moral ambiguity.',
      primaryClusterId: 'cluster-dark-dangerous',
      secondaryClusterIds: ['cluster-magical-worlds', 'cluster-monsters-myth'],
      nearbyNodeIds: [
        'genre-fantasy',
        'romance-dark-romance',
        'romance-monster-romance',
        'subgenre-supernatural-horror',
      ],
      popularStoryEngineIds: [
        'engine-forbidden-love',
        'engine-enemies-to-lovers',
      ],
      popularToneIds: ['tone-dark'],
    },
    {
      id: 'subgenre-mythic-fantasy',
      name: 'Mythic Fantasy',
      slug: 'mythic-fantasy',
      type: 'subgenre',
      parentId: 'genre-fantasy',
      glossaryDefinition:
        'Fantasy inspired by mythology, folklore, legends, or ancient cultural storytelling traditions.',
      shortDefinition: 'Fantasy rooted in myth, folklore, and legend.',
      primaryClusterId: 'cluster-monsters-myth',
      secondaryClusterIds: ['cluster-magical-worlds'],
      nearbyNodeIds: [
        'genre-fantasy',
        'romance-monster-romance',
        'romance-fantasy-romance',
      ],
      popularStoryEngineIds: ['engine-fated-mates', 'engine-forbidden-love'],
      popularToneIds: ['tone-slow-burn'],
    },
    {
      id: 'subgenre-space-opera',
      name: 'Space Opera',
      slug: 'space-opera',
      type: 'subgenre',
      parentId: 'genre-science-fiction',
      glossaryDefinition:
        'Large-scale science fiction adventures involving interstellar conflict, politics, exploration, or war.',
      shortDefinition: 'Large-scale sci-fi adventure across space.',
      primaryClusterId: 'cluster-cosmic-futuristic',
      secondaryClusterIds: ['cluster-high-stakes-suspense'],
      nearbyNodeIds: [
        'genre-science-fiction',
        'romance-science-fiction-romance',
        'romance-alien-romance',
      ],
      popularStoryEngineIds: [
        'engine-forced-proximity',
        'engine-enemies-to-lovers',
      ],
      popularToneIds: ['tone-slow-burn'],
    },
    {
      id: 'subgenre-cyberpunk',
      name: 'Cyberpunk',
      slug: 'cyberpunk',
      type: 'subgenre',
      parentId: 'genre-science-fiction',
      glossaryDefinition:
        'Science fiction set in high-tech futures where advanced technology coexists with societal decay or corporate control.',
      shortDefinition: 'High-tech, dystopian sci-fi shaped by social collapse.',
      primaryClusterId: 'cluster-cosmic-futuristic',
      secondaryClusterIds: ['cluster-dark-dangerous'],
      nearbyNodeIds: [
        'genre-science-fiction',
        'genre-thriller',
        'subgenre-psychological-thriller',
      ],
      popularStoryEngineIds: ['engine-forced-proximity'],
      popularToneIds: ['tone-dark'],
    },
    {
      id: 'subgenre-detective-fiction',
      name: 'Detective Fiction',
      slug: 'detective-fiction',
      type: 'subgenre',
      parentId: 'genre-mystery',
      glossaryDefinition:
        'Mystery stories featuring investigators solving cases through clues, interviews, and deduction.',
      shortDefinition: 'Mystery led by an investigator solving a case.',
      primaryClusterId: 'cluster-high-stakes-suspense',
      secondaryClusterIds: [],
      nearbyNodeIds: [
        'genre-mystery',
        'genre-thriller',
        'romance-romantic-suspense',
      ],
      popularStoryEngineIds: ['engine-forced-proximity'],
      popularToneIds: ['tone-slow-burn'],
    },
    {
      id: 'subgenre-cozy-mystery',
      name: 'Cozy Mystery',
      slug: 'cozy-mystery',
      type: 'subgenre',
      parentId: 'genre-mystery',
      glossaryDefinition:
        'Lighthearted mystery stories, often set in small communities, with softer tension and minimal graphic content.',
      shortDefinition:
        'Lighthearted mystery with softer tension and close communities.',
      primaryClusterId: 'cluster-cozy-comfort',
      secondaryClusterIds: ['cluster-high-stakes-suspense'],
      nearbyNodeIds: [
        'genre-mystery',
        'romance-small-town-romance',
        'romance-romantic-comedy',
      ],
      popularStoryEngineIds: [
        'engine-friends-to-lovers',
        'engine-second-chance',
      ],
      popularToneIds: ['tone-sweet'],
    },
    {
      id: 'subgenre-crime-thriller',
      name: 'Crime Thriller',
      slug: 'crime-thriller',
      type: 'subgenre',
      parentId: 'genre-thriller',
      glossaryDefinition:
        'Thrillers centered on criminal activity, pursuit, danger, and efforts to stop or survive it.',
      shortDefinition: 'Thrillers built around crime, pursuit, and danger.',
      primaryClusterId: 'cluster-high-stakes-suspense',
      secondaryClusterIds: ['cluster-dark-dangerous'],
      nearbyNodeIds: [
        'genre-thriller',
        'genre-mystery',
        'romance-romantic-suspense',
        'romance-mafia-romance',
      ],
      popularStoryEngineIds: [
        'engine-protector-romance',
        'engine-enemies-to-lovers',
      ],
      popularToneIds: ['tone-dark'],
    },
    {
      id: 'subgenre-psychological-thriller',
      name: 'Psychological Thriller',
      slug: 'psychological-thriller',
      type: 'subgenre',
      parentId: 'genre-thriller',
      glossaryDefinition:
        'Thrillers focused on mental tension, manipulation, obsession, and unstable or unreliable perspectives.',
      shortDefinition: 'Thrillers driven by mental tension and manipulation.',
      primaryClusterId: 'cluster-dark-dangerous',
      secondaryClusterIds: ['cluster-high-stakes-suspense'],
      nearbyNodeIds: ['genre-thriller', 'romance-dark-romance', 'genre-horror'],
      popularStoryEngineIds: ['engine-forbidden-love'],
      popularToneIds: ['tone-dark'],
    },
    {
      id: 'subgenre-supernatural-horror',
      name: 'Supernatural Horror',
      slug: 'supernatural-horror',
      type: 'subgenre',
      glossaryDefinition:
        'Horror stories involving supernatural forces, entities, or phenomena that threaten characters.',
      shortDefinition: 'Horror driven by supernatural forces and entities.',
      primaryClusterId: 'cluster-monsters-myth',
      secondaryClusterIds: ['cluster-dark-dangerous'],
      nearbyNodeIds: [
        'subgenre-dark-fantasy',
        'romance-monster-romance',
        'romance-paranormal-romance',
      ],
      popularStoryEngineIds: ['engine-forbidden-love'],
      popularToneIds: ['tone-dark'],
    },

    // ── Romance Subgenres ──────────────────────────────────
    {
      id: 'romance-contemporary-romance',
      name: 'Contemporary Romance',
      slug: 'contemporary-romance',
      type: 'romance_subgenre',
      parentId: 'genre-romance',
      glossaryDefinition: 'Romance stories set in modern-day environments.',
      shortDefinition: 'Romance set in modern-day settings.',
      primaryClusterId: 'cluster-cozy-comfort',
      secondaryClusterIds: [],
      nearbyNodeIds: [
        'genre-romance',
        'romance-romantic-comedy',
        'romance-small-town-romance',
      ],
      popularStoryEngineIds: [
        'engine-friends-to-lovers',
        'engine-second-chance',
      ],
      popularToneIds: ['tone-sweet', 'tone-slow-burn'],
    },
    {
      id: 'romance-fantasy-romance',
      name: 'Fantasy Romance',
      slug: 'fantasy-romance',
      type: 'romance_subgenre',
      parentId: 'genre-romance',
      glossaryDefinition:
        'Romance-driven stories set in fantasy worlds where the relationship is central to the plot.',
      shortDefinition: 'Romance-centered stories set in fantasy worlds.',
      primaryClusterId: 'cluster-magical-worlds',
      secondaryClusterIds: ['cluster-monsters-myth', 'cluster-dark-dangerous'],
      nearbyNodeIds: [
        'genre-fantasy',
        'romance-paranormal-romance',
        'romance-monster-romance',
        'subgenre-dark-fantasy',
      ],
      popularStoryEngineIds: ['engine-fated-mates', 'engine-enemies-to-lovers'],
      popularToneIds: ['tone-slow-burn', 'tone-dark'],
    },
    {
      id: 'romance-paranormal-romance',
      name: 'Paranormal Romance',
      slug: 'paranormal-romance',
      type: 'romance_subgenre',
      parentId: 'genre-romance',
      glossaryDefinition:
        'Romance involving supernatural characters, paranormal abilities, or magical beings.',
      shortDefinition:
        'Romance centered on supernatural characters or abilities.',
      primaryClusterId: 'cluster-monsters-myth',
      secondaryClusterIds: ['cluster-magical-worlds', 'cluster-dark-dangerous'],
      nearbyNodeIds: [
        'romance-fantasy-romance',
        'romance-monster-romance',
        'subgenre-urban-fantasy',
      ],
      popularStoryEngineIds: ['engine-fated-mates', 'engine-forbidden-love'],
      popularToneIds: ['tone-dark', 'tone-slow-burn'],
    },
    {
      id: 'romance-science-fiction-romance',
      name: 'Sci-Fi Romance',
      slug: 'science-fiction-romance',
      type: 'romance_subgenre',
      parentId: 'genre-romance',
      glossaryDefinition:
        'Romance stories set in futuristic, technological, or space-based settings.',
      shortDefinition: 'Romance set in futuristic or space-based worlds.',
      primaryClusterId: 'cluster-cosmic-futuristic',
      secondaryClusterIds: ['cluster-high-stakes-suspense'],
      nearbyNodeIds: [
        'genre-science-fiction',
        'romance-alien-romance',
        'subgenre-space-opera',
      ],
      popularStoryEngineIds: [
        'engine-forced-proximity',
        'engine-enemies-to-lovers',
      ],
      popularToneIds: ['tone-slow-burn'],
    },
    {
      id: 'romance-alien-romance',
      name: 'Alien Romance',
      slug: 'alien-romance',
      type: 'romance_subgenre',
      parentId: 'genre-romance',
      glossaryDefinition:
        'Romance involving extraterrestrial characters, alien cultures, or off-world settings.',
      shortDefinition: 'Romance involving alien characters or cultures.',
      primaryClusterId: 'cluster-cosmic-futuristic',
      secondaryClusterIds: ['cluster-monsters-myth'],
      nearbyNodeIds: [
        'romance-science-fiction-romance',
        'subgenre-space-opera',
        'romance-monster-romance',
      ],
      popularStoryEngineIds: [
        'engine-forced-proximity',
        'engine-forbidden-love',
      ],
      popularToneIds: ['tone-slow-burn'],
    },
    {
      id: 'romance-romantic-suspense',
      name: 'Romantic Suspense',
      slug: 'romantic-suspense',
      type: 'romance_subgenre',
      parentId: 'genre-romance',
      glossaryDefinition:
        'Romance stories where both the relationship and suspense plot are equally central.',
      shortDefinition: 'Romance and suspense developed side by side.',
      primaryClusterId: 'cluster-high-stakes-suspense',
      secondaryClusterIds: ['cluster-dark-dangerous'],
      nearbyNodeIds: [
        'genre-thriller',
        'genre-mystery',
        'subgenre-crime-thriller',
        'romance-dark-romance',
      ],
      popularStoryEngineIds: [
        'engine-protector-romance',
        'engine-forced-proximity',
      ],
      popularToneIds: ['tone-dark', 'tone-slow-burn'],
    },
    {
      id: 'romance-romantic-comedy',
      name: 'Romantic Comedy',
      slug: 'romantic-comedy',
      type: 'romance_subgenre',
      parentId: 'genre-romance',
      glossaryDefinition:
        'Romance stories that emphasize humor, playful conflict, and comedic situations.',
      shortDefinition: 'Romance with a strong humor and banter focus.',
      primaryClusterId: 'cluster-cozy-comfort',
      secondaryClusterIds: [],
      nearbyNodeIds: [
        'romance-contemporary-romance',
        'romance-small-town-romance',
      ],
      popularStoryEngineIds: ['engine-fake-dating', 'engine-opposites-attract'],
      popularToneIds: ['tone-sweet'],
    },
    {
      id: 'romance-dark-romance',
      name: 'Dark Romance',
      slug: 'dark-romance',
      type: 'romance_subgenre',
      parentId: 'genre-romance',
      glossaryDefinition:
        'Romance stories exploring morally complex relationships, darker emotional themes, or dangerous dynamics.',
      shortDefinition:
        'Romance with darker themes, danger, or moral complexity.',
      primaryClusterId: 'cluster-dark-dangerous',
      secondaryClusterIds: ['cluster-monsters-myth'],
      nearbyNodeIds: [
        'romance-mafia-romance',
        'romance-romantic-suspense',
        'subgenre-dark-fantasy',
        'romance-monster-romance',
      ],
      popularStoryEngineIds: [
        'engine-enemies-to-lovers',
        'engine-forbidden-love',
      ],
      popularToneIds: ['tone-dark'],
    },
    {
      id: 'romance-mafia-romance',
      name: 'Mafia Romance',
      slug: 'mafia-romance',
      type: 'romance_subgenre',
      parentId: 'genre-romance',
      glossaryDefinition:
        'Romance stories centered on organized crime, dangerous power structures, and high-stakes relationships.',
      shortDefinition: 'Romance shaped by organized crime and dangerous power.',
      primaryClusterId: 'cluster-dark-dangerous',
      secondaryClusterIds: ['cluster-high-stakes-suspense'],
      nearbyNodeIds: [
        'romance-dark-romance',
        'romance-romantic-suspense',
        'subgenre-crime-thriller',
      ],
      popularStoryEngineIds: [
        'engine-enemies-to-lovers',
        'engine-forbidden-love',
      ],
      popularToneIds: ['tone-dark'],
    },
    {
      id: 'romance-small-town-romance',
      name: 'Small Town Romance',
      slug: 'small-town-romance',
      type: 'romance_subgenre',
      parentId: 'genre-romance',
      glossaryDefinition:
        'Romance stories set in close-knit communities where local relationships and community dynamics shape the story.',
      shortDefinition: 'Romance set in a close-knit community.',
      primaryClusterId: 'cluster-cozy-comfort',
      secondaryClusterIds: [],
      nearbyNodeIds: [
        'romance-contemporary-romance',
        'romance-romantic-comedy',
        'subgenre-cozy-mystery',
      ],
      popularStoryEngineIds: [
        'engine-friends-to-lovers',
        'engine-second-chance',
      ],
      popularToneIds: ['tone-sweet', 'tone-slow-burn'],
    },
    {
      id: 'romance-historical-romance',
      name: 'Historical Romance',
      slug: 'historical-romance',
      type: 'romance_subgenre',
      parentId: 'genre-romance',
      glossaryDefinition:
        'Romance stories set in historical periods, shaped by the customs, expectations, and social structures of the time.',
      shortDefinition: 'Romance set within a historical era.',
      primaryClusterId: 'cluster-historical-worlds',
      secondaryClusterIds: ['cluster-dark-dangerous'],
      nearbyNodeIds: ['genre-historical-fiction'],
      popularStoryEngineIds: [
        'engine-marriage-of-convenience',
        'engine-forbidden-love',
      ],
      popularToneIds: ['tone-slow-burn'],
    },
    {
      id: 'romance-monster-romance',
      name: 'Monster Romance',
      slug: 'monster-romance',
      type: 'romance_subgenre',
      parentId: 'genre-romance',
      glossaryDefinition:
        'Romance stories involving non-human love interests such as monsters, creatures, demons, or mythological beings.',
      shortDefinition: 'Romance involving non-human love interests.',
      primaryClusterId: 'cluster-monsters-myth',
      secondaryClusterIds: ['cluster-magical-worlds', 'cluster-dark-dangerous'],
      nearbyNodeIds: [
        'romance-paranormal-romance',
        'romance-fantasy-romance',
        'romance-alien-romance',
        'subgenre-mythic-fantasy',
      ],
      popularStoryEngineIds: ['engine-fated-mates', 'engine-forbidden-love'],
      popularToneIds: ['tone-dark', 'tone-slow-burn'],
    },

    // ── Story Engines ──────────────────────────────────────
    {
      id: 'engine-enemies-to-lovers',
      name: 'Enemies to Lovers',
      slug: 'enemies-to-lovers',
      type: 'story_engine',
      glossaryDefinition:
        'Two characters begin in opposition and gradually develop romantic attraction.',
      shortDefinition: 'Rivals or enemies become romantic partners.',
      primaryClusterId: 'cluster-dark-dangerous',
      secondaryClusterIds: [
        'cluster-magical-worlds',
        'cluster-high-stakes-suspense',
      ],
      nearbyNodeIds: [
        'romance-fantasy-romance',
        'romance-mafia-romance',
        'romance-romantic-suspense',
      ],
    },
    {
      id: 'engine-fated-mates',
      name: 'Fated Mates',
      slug: 'fated-mates',
      type: 'story_engine',
      glossaryDefinition:
        'Characters are bound together by destiny, biology, magic, or a supernatural force.',
      shortDefinition: 'Characters are bound together by fate or force.',
      primaryClusterId: 'cluster-monsters-myth',
      secondaryClusterIds: ['cluster-magical-worlds'],
      nearbyNodeIds: [
        'romance-fantasy-romance',
        'romance-paranormal-romance',
        'romance-monster-romance',
      ],
    },
    {
      id: 'engine-friends-to-lovers',
      name: 'Friends to Lovers',
      slug: 'friends-to-lovers',
      type: 'story_engine',
      glossaryDefinition:
        'Two characters begin as friends and gradually realize romantic feelings.',
      shortDefinition: 'Friends gradually become romantic partners.',
      primaryClusterId: 'cluster-cozy-comfort',
      secondaryClusterIds: [],
      nearbyNodeIds: [
        'romance-small-town-romance',
        'romance-contemporary-romance',
      ],
    },
    {
      id: 'engine-second-chance',
      name: 'Second Chance',
      slug: 'second-chance',
      type: 'story_engine',
      glossaryDefinition:
        'Former lovers or almost-lovers reconnect and attempt to rebuild their relationship.',
      shortDefinition: 'Past lovers reconnect and try again.',
      primaryClusterId: 'cluster-cozy-comfort',
      secondaryClusterIds: ['cluster-historical-worlds'],
      nearbyNodeIds: [
        'romance-small-town-romance',
        'romance-contemporary-romance',
        'romance-historical-romance',
      ],
    },
    {
      id: 'engine-fake-dating',
      name: 'Fake Dating',
      slug: 'fake-dating',
      type: 'story_engine',
      glossaryDefinition:
        'Characters pretend to be in a relationship for a specific reason, leading to real feelings.',
      shortDefinition: 'A pretend relationship turns real.',
      primaryClusterId: 'cluster-cozy-comfort',
      secondaryClusterIds: [],
      nearbyNodeIds: [
        'romance-romantic-comedy',
        'romance-contemporary-romance',
      ],
    },
    {
      id: 'engine-opposites-attract',
      name: 'Opposites Attract',
      slug: 'opposites-attract',
      type: 'story_engine',
      glossaryDefinition:
        'Two characters with contrasting personalities or lifestyles develop a romantic relationship.',
      shortDefinition: 'Contrasting personalities create romantic chemistry.',
      primaryClusterId: 'cluster-cozy-comfort',
      secondaryClusterIds: [],
      nearbyNodeIds: [
        'romance-romantic-comedy',
        'romance-contemporary-romance',
      ],
    },
    {
      id: 'engine-forced-proximity',
      name: 'Forced Proximity',
      slug: 'forced-proximity',
      type: 'story_engine',
      glossaryDefinition:
        'Characters are placed in situations where they must remain physically or socially close.',
      shortDefinition: 'Characters are pushed into unavoidable closeness.',
      primaryClusterId: 'cluster-high-stakes-suspense',
      secondaryClusterIds: ['cluster-cosmic-futuristic'],
      nearbyNodeIds: [
        'romance-romantic-suspense',
        'romance-science-fiction-romance',
      ],
    },
    {
      id: 'engine-protector-romance',
      name: 'Protector Romance',
      slug: 'protector-romance',
      type: 'story_engine',
      glossaryDefinition:
        'One character is responsible for protecting the other, creating dependence, trust, and emotional tension.',
      shortDefinition: 'Protection creates closeness, trust, and tension.',
      primaryClusterId: 'cluster-high-stakes-suspense',
      secondaryClusterIds: ['cluster-dark-dangerous'],
      nearbyNodeIds: ['romance-romantic-suspense', 'romance-mafia-romance'],
    },
    {
      id: 'engine-forbidden-love',
      name: 'Forbidden Love',
      slug: 'forbidden-love',
      type: 'story_engine',
      glossaryDefinition:
        'The romantic relationship is restricted by social rules, danger, morality, or circumstance.',
      shortDefinition:
        'Love that is blocked by rules, danger, or circumstance.',
      primaryClusterId: 'cluster-dark-dangerous',
      secondaryClusterIds: [
        'cluster-historical-worlds',
        'cluster-monsters-myth',
      ],
      nearbyNodeIds: [
        'romance-dark-romance',
        'romance-historical-romance',
        'romance-monster-romance',
      ],
    },
    {
      id: 'engine-marriage-of-convenience',
      name: 'Marriage of Convenience',
      slug: 'marriage-of-convenience',
      type: 'story_engine',
      glossaryDefinition:
        'Characters enter a relationship or marriage for practical reasons rather than love.',
      shortDefinition: 'A practical arrangement becomes romance.',
      primaryClusterId: 'cluster-historical-worlds',
      secondaryClusterIds: ['cluster-magical-worlds'],
      nearbyNodeIds: ['romance-historical-romance', 'romance-fantasy-romance'],
    },

    // ── Tones ──────────────────────────────────────────────
    {
      id: 'tone-dark',
      name: 'Dark',
      slug: 'dark',
      type: 'tone',
      glossaryDefinition:
        'Stories with heavier emotional intensity, danger, moral ambiguity, or oppressive atmosphere.',
      shortDefinition: 'Heavier emotional intensity and darker atmosphere.',
      primaryClusterId: 'cluster-dark-dangerous',
      secondaryClusterIds: ['cluster-monsters-myth'],
      nearbyNodeIds: [
        'romance-dark-romance',
        'subgenre-dark-fantasy',
        'subgenre-psychological-thriller',
      ],
    },
    {
      id: 'tone-slow-burn',
      name: 'Slow Burn',
      slug: 'slow-burn',
      type: 'tone',
      glossaryDefinition:
        'Romantic or emotional tension develops gradually over the course of the story.',
      shortDefinition: 'Tension develops gradually over time.',
      primaryClusterId: 'cluster-cozy-comfort',
      secondaryClusterIds: [
        'cluster-magical-worlds',
        'cluster-historical-worlds',
      ],
      nearbyNodeIds: [
        'romance-fantasy-romance',
        'romance-small-town-romance',
        'romance-historical-romance',
      ],
    },
    {
      id: 'tone-sweet',
      name: 'Sweet',
      slug: 'sweet',
      type: 'tone',
      glossaryDefinition:
        'Stories with lighter emotional tone, softer romantic conflict, and minimal explicitness.',
      shortDefinition: 'Lighter, softer, and less explicit in tone.',
      primaryClusterId: 'cluster-cozy-comfort',
      secondaryClusterIds: [],
      nearbyNodeIds: [
        'romance-romantic-comedy',
        'romance-small-town-romance',
        'subgenre-cozy-mystery',
      ],
    },
  ],

  gravityEdges: [
    {
      id: 'edge-fantasy-romance-paranormal-romance',
      sourceNodeId: 'romance-fantasy-romance',
      targetNodeId: 'romance-paranormal-romance',
      relationshipType: 'adjacent_subgenre',
      weight: 0.89,
      reason:
        'Both frequently overlap in magical and supernatural romance discovery.',
    },
    {
      id: 'edge-fantasy-romance-fated-mates',
      sourceNodeId: 'romance-fantasy-romance',
      targetNodeId: 'engine-fated-mates',
      relationshipType: 'shared_story_engine',
      weight: 0.87,
      reason:
        'Fated Mates is one of the strongest recurring engines in fantasy romance.',
    },
    {
      id: 'edge-fantasy-romance-enemies-to-lovers',
      sourceNodeId: 'romance-fantasy-romance',
      targetNodeId: 'engine-enemies-to-lovers',
      relationshipType: 'co_occurs_with',
      weight: 0.78,
      reason:
        'Enemies to Lovers commonly appears in fantasy romance story stacks.',
    },
    {
      id: 'edge-fantasy-romance-monster-romance',
      sourceNodeId: 'romance-fantasy-romance',
      targetNodeId: 'romance-monster-romance',
      relationshipType: 'same_mood_cluster',
      weight: 0.72,
      reason:
        'Both are commonly explored within magical or creature-driven story spaces.',
    },
    {
      id: 'edge-small-town-friends-to-lovers',
      sourceNodeId: 'romance-small-town-romance',
      targetNodeId: 'engine-friends-to-lovers',
      relationshipType: 'shared_story_engine',
      weight: 0.84,
      reason: 'Friends to Lovers is highly associated with small-town romance.',
    },
    {
      id: 'edge-small-town-second-chance',
      sourceNodeId: 'romance-small-town-romance',
      targetNodeId: 'engine-second-chance',
      relationshipType: 'shared_story_engine',
      weight: 0.79,
      reason:
        'Second Chance is a common emotional engine in small-town romance.',
    },
    {
      id: 'edge-small-town-romcom',
      sourceNodeId: 'romance-small-town-romance',
      targetNodeId: 'romance-romantic-comedy',
      relationshipType: 'adjacent_subgenre',
      weight: 0.68,
      reason:
        'Both often share lighter tone, community settings, and comedic or warm dynamics.',
    },
    {
      id: 'edge-romantic-suspense-thriller',
      sourceNodeId: 'romance-romantic-suspense',
      targetNodeId: 'genre-thriller',
      relationshipType: 'adjacent_subgenre',
      weight: 0.86,
      reason:
        'Romantic Suspense strongly overlaps with thriller pacing and danger structures.',
    },
    {
      id: 'edge-romantic-suspense-protector',
      sourceNodeId: 'romance-romantic-suspense',
      targetNodeId: 'engine-protector-romance',
      relationshipType: 'shared_story_engine',
      weight: 0.8,
      reason: 'Protector dynamics are highly common in romantic suspense.',
    },
    {
      id: 'edge-romantic-suspense-forced-proximity',
      sourceNodeId: 'romance-romantic-suspense',
      targetNodeId: 'engine-forced-proximity',
      relationshipType: 'co_occurs_with',
      weight: 0.74,
      reason: 'Danger-based plots often create forced proximity conditions.',
    },
    {
      id: 'edge-dark-romance-mafia-romance',
      sourceNodeId: 'romance-dark-romance',
      targetNodeId: 'romance-mafia-romance',
      relationshipType: 'adjacent_subgenre',
      weight: 0.82,
      reason:
        'These subgenres often overlap in tone, power dynamics, and reader discovery.',
    },
    {
      id: 'edge-dark-romance-forbidden-love',
      sourceNodeId: 'romance-dark-romance',
      targetNodeId: 'engine-forbidden-love',
      relationshipType: 'shared_story_engine',
      weight: 0.71,
      reason: 'Forbidden Love is a common engine in darker romance stories.',
    },
    {
      id: 'edge-monster-romance-paranormal-romance',
      sourceNodeId: 'romance-monster-romance',
      targetNodeId: 'romance-paranormal-romance',
      relationshipType: 'adjacent_subgenre',
      weight: 0.88,
      reason:
        'Monster Romance and Paranormal Romance strongly overlap in supernatural relationship discovery.',
    },
    {
      id: 'edge-monster-romance-fated-mates',
      sourceNodeId: 'romance-monster-romance',
      targetNodeId: 'engine-fated-mates',
      relationshipType: 'shared_story_engine',
      weight: 0.71,
      reason:
        'Fated Mates commonly appears in monster and creature-centered romance.',
    },
    {
      id: 'edge-alien-romance-sci-fi-romance',
      sourceNodeId: 'romance-alien-romance',
      targetNodeId: 'romance-science-fiction-romance',
      relationshipType: 'adjacent_subgenre',
      weight: 0.9,
      reason:
        'Alien Romance is one of the closest romance variants to Sci-Fi Romance.',
    },
    {
      id: 'edge-alien-romance-forced-proximity',
      sourceNodeId: 'romance-alien-romance',
      targetNodeId: 'engine-forced-proximity',
      relationshipType: 'co_occurs_with',
      weight: 0.69,
      reason:
        'Travel, captivity, survival, or off-world settings often create forced proximity.',
    },
  ],

  constellationViews: [
    {
      clusterId: 'cluster-magical-worlds',
      centerNodeId: 'genre-fantasy',
      visibleNodeIds: [
        'genre-fantasy',
        'subgenre-epic-fantasy',
        'subgenre-urban-fantasy',
        'subgenre-dark-fantasy',
        'subgenre-mythic-fantasy',
        'romance-fantasy-romance',
        'romance-paranormal-romance',
        'romance-monster-romance',
        'engine-fated-mates',
        'engine-enemies-to-lovers',
        'tone-slow-burn',
        'tone-dark',
      ],
    },
    {
      clusterId: 'cluster-cozy-comfort',
      centerNodeId: 'genre-romance',
      visibleNodeIds: [
        'genre-romance',
        'romance-contemporary-romance',
        'romance-romantic-comedy',
        'romance-small-town-romance',
        'subgenre-cozy-mystery',
        'engine-friends-to-lovers',
        'engine-second-chance',
        'engine-fake-dating',
        'engine-opposites-attract',
        'tone-sweet',
        'tone-slow-burn',
      ],
    },
    {
      clusterId: 'cluster-high-stakes-suspense',
      centerNodeId: 'genre-thriller',
      visibleNodeIds: [
        'genre-thriller',
        'genre-mystery',
        'subgenre-crime-thriller',
        'subgenre-detective-fiction',
        'romance-romantic-suspense',
        'engine-protector-romance',
        'engine-forced-proximity',
        'engine-enemies-to-lovers',
        'tone-dark',
        'tone-slow-burn',
      ],
    },
  ],

  suggestedStoryStacks: [
    {
      id: 'stack-1',
      label: 'Dark Fated Mates Fantasy Romance',
      nodeIds: ['tone-dark', 'engine-fated-mates', 'romance-fantasy-romance'],
    },
    {
      id: 'stack-2',
      label: 'Small Town Friends to Lovers',
      nodeIds: ['romance-small-town-romance', 'engine-friends-to-lovers'],
    },
    {
      id: 'stack-3',
      label: 'Romantic Suspense Protector Romance',
      nodeIds: ['romance-romantic-suspense', 'engine-protector-romance'],
    },
  ],
}

// Helper Functions
export const getNodeById = (id: string): StoryGalaxyNode | undefined =>
  storyGalaxyData.nodes.find((n) => n.id === id)

export const getClusterById = (id: string): MoodCluster | undefined =>
  storyGalaxyData.moodClusters.find((c) => c.id === id)

export const getEdgesForNode = (nodeId: string): GravityEdge[] =>
  storyGalaxyData.gravityEdges.filter(
    (e) => e.sourceNodeId === nodeId || e.targetNodeId === nodeId,
  )

export const getNodesForCluster = (clusterId: string): StoryGalaxyNode[] =>
  storyGalaxyData.nodes.filter(
    (n) =>
      n.primaryClusterId === clusterId ||
      n.secondaryClusterIds.includes(clusterId),
  )

export const getConstellationView = (
  clusterId: string,
): ConstellationView | undefined =>
  storyGalaxyData.constellationViews.find((cv) => cv.clusterId === clusterId)
